import { StateContext } from '@ngxs/store';

/**
 * What the states hand to `patchState`: either a Typesaurus `{ data, ref }` document,
 * or an already flattened entity that only carries its `ref` (see StorageState.addStorageItem).
 */
export interface PatchableDocument {
  data?: object;
  ref?: unknown;
}

// No constraint: `Record<string, unknown>` rejects every `interface XStateModel` caller,
// because interfaces get no implicit index signature (TS2344), and the state model is only
// ever spread and indexed by `keyof T` here, so nothing needs narrowing.
export const patchState = <T>(
  ctx: StateContext<T>,
  key: keyof T,
  data: Array<PatchableDocument | null | undefined>,
  callback: (document: PatchableDocument) => object = ({ data: documentData, ref }) => ({ ...documentData, ref }),
) => {
  // console.log('patchState', { [key]: data });
  // A plain spread rather than `patch()`: the spec always carried every key of the
  // state anyway, and `[key]` is a freshly mapped array on every call, so `patch()`
  // could never take its "nothing changed, keep the old reference" path.
  return ctx.setState((state: Readonly<T>) => ({
    ...state,
    // The predicate is explicit because `Boolean(document)` alone does not give the
    // callback the narrowed `PatchableDocument` that `.map(callback)` needs.
    [key]: data.filter((document): document is PatchableDocument => Boolean(document)).map(callback),
    isLoading: false,
  }));
};
